import React from "react";
import { WORLDS, HOTSPOTS, NEEDS_YOU, STATUS } from "../config/worlds";
import mapArt from "../assets/kingdom-map.jpg";

/**
 * The map: one camera transform over one image, hotspots + badges on top.
 * `focus` is null | "king" | "leads" | "clients" | "internal".
 */
export default function KingdomMap({ focus, onFocus, byWorld, needsYou }) {
  const focused = Boolean(focus);
  const cameraStyle = { transform: "none" };

  const worldStats = (k) => {
    const set = byWorld[k] ?? [];
    const need = set.filter((a) => NEEDS_YOU.includes(a.status)).length;
    const blocked = set.some((a) => a.status === STATUS.BLOCKED);
    return { count: set.length, need, blocked };
  };
  const totalNeed = needsYou.length;

  return (
    // cover, not contain: the map always fills the full width (cropping top/bottom
    // on short viewports) so no background shows at the sides. cqh = this box's height.
    <div className="absolute inset-0 grid place-items-center overflow-hidden [container-type:size]">
      <div
        className="relative w-[max(100cqw,calc(100cqh*1.7768))] shrink-0 aspect-[1672/941] transition-transform duration-1000 [transition-timing-function:cubic-bezier(.32,.72,.24,1)] will-change-transform"
        style={cameraStyle}
      >
        <img
          src={mapArt}
          alt="The BBA Agent Kingdom — the King above the Leads, Clients, and Internal worlds"
          className="absolute inset-0 h-full w-full select-none"
          draggable={false}
        />

        {/* dim veil when focused */}
        <div
          className={`pointer-events-none absolute inset-0 z-[4] bg-[#0C203A]/30 transition-opacity duration-700 ${
            focused ? "opacity-100" : "opacity-0"
          }`}
        />

        {/* King hotspot */}
        <Hotspot
          spot={HOTSPOTS.king}
          hidden={focused}
          onClick={() => onFocus("king")}
          label="Open the King's overview"
        >
          <Badge pos="top" urgent={totalNeed > 0}>
            <span>👑 The King</span>
            <NeedPill n={totalNeed} />
          </Badge>
        </Hotspot>

        {/* World hotspots */}
        {Object.keys(WORLDS).map((k) => {
          const s = worldStats(k);
          return (
            <Hotspot
              key={k}
              spot={HOTSPOTS[k]}
              hidden={focused}
              onClick={() => onFocus(k)}
              label={`Enter ${WORLDS[k].name}`}
            >
              <Badge pos={HOTSPOTS[k].badge} urgent={s.need > 0} bad={s.blocked}>
                <span className="font-medium text-sky-200/90">{s.count} agents</span>
                <NeedPill n={s.need} />
              </Badge>
            </Hotspot>
          );
        })}
      </div>
    </div>
  );
}

function Hotspot({ spot, hidden, onClick, label, children }) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      className={`group absolute z-[5] rounded-3xl transition duration-200 hover:scale-[1.015] focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-amber-400 ${
        hidden ? "pointer-events-none opacity-0" : ""
      }`}
      style={{
        left: `${spot.left}%`,
        top: `${spot.top}%`,
        width: `${spot.width}%`,
        height: `${spot.height}%`,
      }}
    >
      <span className="pointer-events-none absolute -inset-1 rounded-[28px] border-[3px] border-transparent transition duration-200 group-hover:border-white/85 group-hover:shadow-[0_0_30px_6px_rgba(255,255,255,.45),inset_0_0_40px_rgba(255,255,255,.25)]" />
      {children}
    </button>
  );
}

function Badge({ pos, urgent, bad, children }) {
  return (
    <span
      className={`pointer-events-none absolute left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full border-2 bg-[#122A4A]/90 px-3 py-1 font-display text-[12.5px] font-semibold text-white shadow-lg transition-opacity duration-500 ${
        pos === "top" ? "-top-2" : "-bottom-2"
      } ${
        urgent
          ? `animate-badge-bob ${bad ? "border-rose-400/80" : "border-amber-300/80"}`
          : "border-white/20"
      }`}
    >
      <span className="inline-flex items-center gap-2">{children}</span>
    </span>
  );
}

function NeedPill({ n }) {
  return (
    <span
      className={`rounded-full px-2 py-px text-[11.5px] font-bold ${
        n > 0 ? "bg-amber-400 text-amber-950" : "bg-emerald-400 text-emerald-950"
      }`}
    >
      {n}
    </span>
  );
}
